import type {
  Float8_Comparison_Exp,
  LocationsStudiesQueryVariables,
} from 'graphql/research/types-and-hooks';
import type { Locations, Location } from './cloropleth-map';

import React, { useMemo, useState } from 'react';
import { useToast, Card, Paragraph } from '@leafwell/components';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { useLocationsStudiesQuery } from 'graphql/research/types-and-hooks';
import { DASHBOARD_FILTERS } from 'lib/queries/dashboard';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useDasboardContext } from 'contexts/dashboard-context';

import Loader from './loader';
import LineCounter from './line-counter';
import HeadingBody from './heading-body';

const CloroplethMap = dynamic(() => import('./cloropleth-map'), {
  ssr: false,
});

const LocationItem: React.FC<
  Location & { isUnique?: boolean; total: number }
> = ({
  location,
  studies: {
    aggregate: { count },
  },
  isUnique = false,
  total,
}) => {
  const { translations } = useWordPressContext();

  return isUnique ? (
    <li>
      <header>
        <HeadingBody
          {...{
            level: '4',
            title: `${location} ${translations['studies']}`,
          }}
        />
        <Paragraph size="small">
          {translations['There is a total of %d studies on cannabis for %s.']
            .replace('%d', count.toString())
            .replace('%s', location)}
        </Paragraph>
      </header>
      <div className="flex gap-3 pt-6">
        <HeadingBody
          {...{
            level: '2',
            title: count.toString(),
            tagName: 'span',
          }}
        />
        <Image src="/document-search.svg" width={43} height={39} alt="" />
      </div>
    </li>
  ) : (
    <li className="grid grid-cols-inline-form justify-between gap-x-6 gap-y-1 leading-tight text-sm">
      {location}
      <span>{count}</span>
      <LineCounter
        {...{
          className: 'col-span-full',
          color: 'lilac-300',
          count,
          total,
        }}
      />
    </li>
  );
};

const DashboardLocations: React.FC<{ id: string }> = ({ id }) => {
  const { addToast } = useToast();
  const { translations } = useWordPressContext();
  const {
    filters,
    initialData: { [DASHBOARD_FILTERS.LOCATIONS]: initialData },
    studies: { byYear, total },
  } = useDasboardContext();
  const [locations, updateLocations] = useState<Locations>(initialData);

  const locationsFilter = filters?.[DASHBOARD_FILTERS.LOCATIONS]?.location_id;
  const isUniqueLocation = locationsFilter?._in?.length === 1;

  const studyIds = useMemo(() => byYear.map(({ id }) => id), [byYear]);

  const variables: LocationsStudiesQueryVariables = useMemo(
    () => ({
      ...(locationsFilter
        ? {
            locationIds: locationsFilter,
          }
        : {}),
      ...(studyIds
        ? { studyIds: { _in: studyIds } as Float8_Comparison_Exp }
        : {}),
    }),
    [locationsFilter, studyIds],
  );

  const { loading } = useLocationsStudiesQuery({
    skip: !filters,
    variables,
    onCompleted: data => {
      updateLocations(data.nodes);
    },
    onError: error => {
      addToast({
        content: error.message,
        key: 'dashboard-locations-error',
        type: 'negative',
        shouldRemove: true,
      });
    },
  });

  useMemo(() => {
    if (!filters) {
      updateLocations(initialData);
    }
  }, [filters]);

  return (
    <section {...{ id, className: 'grid lg:grid-cols-3 gap-10' }}>
      <Card
        {...{
          className: [
            'md:gap-y-0 lg:col-span-2',
            isUniqueLocation ? 'hidden md:block' : '',
          ].join(' '),
          filled: true,
          rounded: true,
          variant: 'light',
        }}
      >
        <Paragraph
          {...{ className: 'self-start', size: 'large', tagName: 'header' }}
        >
          {translations['Studies by Country']}
        </Paragraph>
        {loading ? (
          <Loader fullHeight={false} />
        ) : (
          <CloroplethMap
            {...{
              className: 'sm:-mt-6',
              data: locations,
            }}
          />
        )}
      </Card>
      <Card
        {...{
          className: 'content-start',
          rounded: true,
          variant: 'secondary',
        }}
      >
        {loading ? (
          <Loader fullHeight={false} />
        ) : (
          <>
            {!isUniqueLocation && (
              <Paragraph size="large" tagName="header">
                {translations['Countries with most studies']}
              </Paragraph>
            )}
            <ul className="grid gap-4">
              {locations.slice(0, 11).map((item, key) => (
                <LocationItem
                  {...{
                    ...item,
                    key,
                    isUnique: isUniqueLocation,
                    total,
                  }}
                />
              ))}
            </ul>
          </>
        )}
      </Card>
    </section>
  );
};

export default DashboardLocations;
